import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import fileTextIcon from '../../assets/img3d/contrat.png';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';
import ScheduleVisitDialog from '../../components/admin/ScheduleVisitDialog';
import CommunicationPanel from '../../components/admin/CommunicationPanel';
import { StartConversationButton } from '../../components/messaging';
import {
  User, Mail, Phone, MapPin, Euro, Calendar, TrendingUp,
  CheckCircle, Clock, AlertCircle, MessageSquare, ArrowLeft,
  UserPlus, Building2, Edit, Save, XCircle, Send
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactRequestDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [visitSlots, setVisitSlots] = useState([]);
  const [selectedSlotId, setSelectedSlotId] = useState('');
  const [manualVisitDate, setManualVisitDate] = useState('');
  const [manualVisitDuration, setManualVisitDuration] = useState(60);
  const [manualVisitNotes, setManualVisitNotes] = useState('');
  
  const [formData, setFormData] = useState({
    status: '',
    admin_notes: '',
    assigned_to_professional: ''
  });

  const [communicationForm, setCommunicationForm] = useState({
    type: 'email',
    subject: '',
    message: ''
  });

  useEffect(() => {
    setRetryCount(0); // Reset retry count when ID changes
    fetchRequest();
  }, [id]);

  // TODO: Réactiver quand la logique de score sera implémentée
  // Retry fetching if scoreDetails is null (backend will recalculate on second request)
  // Only retry once to avoid infinite loops
  // useEffect(() => {
  //   if (request && 
  //       (!request.score_details || 
  //        (request.score_details.budget_match === 0 && 
  //         request.score_details.financing === 0 && 
  //         request.score_details.urgency === 0 && 
  //         request.score_details.criteria_match === 0 && 
  //         request.score_details.profile_quality === 0)) && 
  //       request.score > 0 && 
  //       retryCount < 1) {
  //     console.log('🔄 scoreDetails is missing/empty but score exists, retrying fetch in 1 second...');
  //     const timer = setTimeout(() => {
  //       setRetryCount(prev => prev + 1);
  //       fetchRequest();
  //     }, 1000);
  //     return () => clearTimeout(timer);
  //   }
  // }, [request, retryCount]);

  const fetchRequest = async () => {
    try {
      const response = await axios.get(`${API}/admin/contact-requests/${id}`);
      const data = response.data;
      
      // TODO: Réactiver quand la logique de score sera implémentée
      // Debug: Log the raw data to see what we're getting
      // console.log('🔍 Raw API response:', {
      //   score: data.score,
      //   scoreDetails: data.scoreDetails,
      //   scoreDetailsType: typeof data.scoreDetails,
      //   scoreDetailsValue: data.scoreDetails
      // });
      
      // Transform camelCase API response to snake_case format expected by frontend
      const transformedRequest = {
        ...data,
        // Convert camelCase to snake_case for all fields
        seeker_name: data.seekerName || data.seeker_name,
        seeker_email: data.seekerEmail || data.seeker_email,
        seeker_phone: data.seekerPhone || data.seeker_phone,
        seeker_message: data.seekerMessage || data.seeker_message,
        property_id: data.propertyId || data.property_id,
        // Convert Decimal to number for budget fields
        budget_min: data.budgetMin ? Number(data.budgetMin) : (data.budget_min ? Number(data.budget_min) : null),
        budget_max: data.budgetMax ? Number(data.budgetMax) : (data.budget_max ? Number(data.budget_max) : null),
        financing_status: data.financingStatus || data.financing_status,
        move_in_date: data.moveInDate || data.move_in_date,
        current_situation: data.currentSituation || data.current_situation,
        has_property_to_sell: data.hasPropertyToSell !== undefined ? data.hasPropertyToSell : data.has_property_to_sell,
        assigned_to_admin: data.assignedToAdminId || data.assigned_to_admin,
        assigned_to_admin_id: data.assignedToAdminId || data.assigned_to_admin_id,
        admin_notes: data.adminNotes || data.admin_notes,
        qualification_date: data.qualificationDate || data.qualification_date,
        assigned_to_professional: data.assignedToProfessionalId || data.assigned_to_professional,
        assigned_to_professional_id: data.assignedToProfessionalId || data.assigned_to_professional_id,
        assignment_date: data.assignmentDate || data.assignment_date,
        visit_scheduled: data.visitScheduled !== undefined ? data.visitScheduled : data.visit_scheduled,
        visit_date: data.visitDate || data.visit_date,
        visit_duration: data.visitDuration || data.visit_duration,
        visit_notes: data.visitNotes || data.visit_notes,
        visit_completed: data.visitCompleted !== undefined ? data.visitCompleted : data.visit_completed,
        visit_outcome: data.visitOutcome || data.visit_outcome,
        visit_feedback: data.visitFeedback || data.visit_feedback,
        next_steps: data.nextSteps || data.next_steps,
        created_at: data.createdAt || data.created_at,
        updated_at: data.updatedAt || data.updated_at,
        // TODO: Réactiver quand la logique de score sera implémentée
        // Transform scoreDetails (camelCase) to score_details (snake_case)
        // Handle both JSON string and object formats from Prisma
        // score_details: (() => {
        //   // Use nullish coalescing (??) instead of || to handle null values correctly
        //   let scoreDetails = data.scoreDetails ?? data.score_details;
        //   
        //   console.log('🔍 Processing scoreDetails:', {
        //     raw: scoreDetails,
        //     type: typeof scoreDetails,
        //     isNull: scoreDetails === null,
        //     isUndefined: scoreDetails === undefined,
        //     dataScoreDetails: data.scoreDetails,
        //     dataScore_details: data.score_details
        //   });
        //   
        //   // If it's a string, parse it
        //   if (typeof scoreDetails === 'string') {
        //     try {
        //       scoreDetails = JSON.parse(scoreDetails);
        //       console.log('✅ Parsed JSON string:', scoreDetails);
        //     } catch (e) {
        //       console.error('❌ Error parsing scoreDetails JSON:', e);
        //       scoreDetails = null;
        //     }
        //   }
        //   
        //   // If scoreDetails exists, extract values with fallback for both naming conventions
        //   if (scoreDetails && typeof scoreDetails === 'object' && scoreDetails !== null) {
        //     const transformed = {
        //       budget_match: Number(scoreDetails.budgetMatch ?? scoreDetails.budget_match ?? 0),
        //       financing: Number(scoreDetails.financing ?? 0),
        //       urgency: Number(scoreDetails.urgency ?? 0),
        //       criteria_match: Number(scoreDetails.criteriaMatch ?? scoreDetails.criteria_match ?? 0),
        //       profile_quality: Number(scoreDetails.profileQuality ?? scoreDetails.profile_quality ?? 0),
        //     };
        //     console.log('✅ Transformed scoreDetails:', transformed);
        //     return transformed;
        //   }
        //   
        //   // If scoreDetails is null, the backend should have recalculated it
        //   // But if it's still null, we need to wait for the backend to recalculate
        //   console.warn('⚠️ scoreDetails is null/undefined - backend should recalculate on next request');
        //   return {
        //     budget_match: 0,
        //     financing: 0,
        //     urgency: 0,
        //     criteria_match: 0,
        //     profile_quality: 0,
        //   };
        // })(),
        messages: data.messages || data.messages || [],
      };
      
      // TODO: Réactiver quand la logique de score sera implémentée
      // console.log('✅ Final transformed request score_details:', transformedRequest.score_details);
      // console.log('✅ Final transformed request score:', transformedRequest.score);
      
      // Extract property from response (already included with owner)
      const propertyData = data.property || transformedRequest.property;
      if (propertyData) {
      setProperty(propertyData);
      const slots = propertyData.visitSlots || propertyData.visit_slots || [];
      setVisitSlots(Array.isArray(slots) ? slots : []);
      }

      setRequest(transformedRequest);
      
      // Pre-fill assigned_to_professional with property owner ID (the professional who created the property)
      const propertyOwnerId = propertyData?.ownerId || propertyData?.owner?.id || null;
      setFormData({
        status: transformedRequest.status,
        admin_notes: transformedRequest.admin_notes || '',
        assigned_to_professional: transformedRequest.assigned_to_professional_id || propertyOwnerId || ''
      });
    } catch (error) {
      console.error('Error fetching request:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger la demande',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const minutesToTime = (minutes) => {
    if (minutes === null || minutes === undefined) return '';
    const h = Math.floor(minutes / 60).toString().padStart(2, '0');
    const m = (minutes % 60).toString().padStart(2, '0');
    return `${h}:${m}`;
  };

  const handleProposeVisit = async () => {
    if (!id) return;
    try {
      const params = new URLSearchParams();
      if (selectedSlotId) params.append('visitSlotId', selectedSlotId);
      if (manualVisitDate) params.append('visitDate', manualVisitDate);
      if (manualVisitDuration) params.append('visitDuration', manualVisitDuration);
      if (manualVisitNotes) params.append('visitNotes', manualVisitNotes);

      await axios.post(`${API}/admin/contact-requests/${id}/propose-visit?${params.toString()}`);
      toast({
        title: 'Proposition envoyée',
        description: 'Le client pourra confirmer ce créneau',
      });
      await fetchRequest();
    } catch (error) {
      console.error('Error proposing visit:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.message || 'Impossible de proposer la visite',
        variant: 'destructive',
      });
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(`${API}/admin/contact-requests/${id}`, {
        status: formData.status,
        admin_notes: formData.admin_notes
      });
      
      toast({
        title: 'Mise à jour réussie',
        description: 'Les modifications ont été enregistrées'
      });
      
      setEditMode(false);
      fetchRequest();
    } catch (error) {
      console.error('Error updating request:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour',
        variant: 'destructive'
      });
    }
  };

  const handleAssignToMe = async () => {
    try {
      await axios.post(`${API}/admin/contact-requests/${id}/assign-to-me`);
      toast({
        title: 'Assignation réussie',
        description: 'La demande vous a été assignée'
      });
      fetchRequest();
    } catch (error) {
      console.error('Error assigning:', error);
    }
  };

  const handleAssignToProfessional = async () => {
    // Get the property owner ID (the professional who created the property)
    const propertyOwnerId = property?.ownerId || property?.owner?.id;
    
    if (!propertyOwnerId) {
      toast({
        title: 'Erreur',
        description: 'Impossible de déterminer le professionnel propriétaire du bien',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/contact-requests/${id}/assign-to-professional?professionalId=${propertyOwnerId}`);
      const { conversationId } = response.data;
      
      toast({
        title: 'Assignation réussie',
        description: 'La demande a été assignée et un message a été envoyé au professionnel.',
        action: conversationId ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/messages')}
          >
            Voir la conversation
          </Button>
        ) : undefined
      });
      fetchRequest();
    } catch (error) {
      console.error('Error assigning to professional:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'assigner au professionnel',
        variant: 'destructive'
      });
    }
  };

  const handleAddCommunication = async () => {
    if (!communicationForm.message) {
      toast({
        title: 'Erreur',
        description: 'Le message est requis',
        variant: 'destructive'
      });
      return;
    }

    try {
      await axios.post(`${API}/admin/contact-requests/${id}/communications`, {
        type: communicationForm.type,
        from_party: 'admin',
        to_party: 'seeker',
        subject: communicationForm.subject,
        message: communicationForm.message,
        attachments: []
      });

      toast({
        title: 'Communication ajoutée',
        description: 'Le message a été enregistré'
      });

      setCommunicationForm({ type: 'email', subject: '', message: '' });
      fetchRequest();
    } catch (error) {
      console.error('Error adding communication:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'ajouter la communication',
        variant: 'destructive'
      });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  // TODO: Réactiver quand la logique de score sera implémentée
  // const getScoreBadge = (score) => {
  //   if (score >= 75) {
  //     return <Badge className="bg-red-600 text-white text-lg px-4 py-1">🔥 {score}/100</Badge>;
  //   } else if (score >= 50) {
  //     return <Badge className="bg-yellow-600 text-white text-lg px-4 py-1">⚡ {score}/100</Badge>;
  //   } else {
  //     return <Badge className="bg-gray-600 text-white text-lg px-4 py-1">❄️ {score}/100</Badge>;
  //   }
  // };

  if (loading) {
    return (
      <Layout
        headerConfig={{
          icon: fileTextIcon,
          iconColor: 'bg-white',
          title: 'Chargement...',
          subtitle: 'Chargement de la demande'
        }}
      >
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#FF9F68]"></div>
        </div>
      </Layout>
    );
  }

  if (!request) {
    return (
      <Layout
        headerConfig={{
          icon: fileTextIcon,
          iconColor: 'bg-white',
          title: 'Demande non trouvée',
          subtitle: 'La demande demandée n\'existe pas',
          primaryAction: 'Retour aux demandes',
          onPrimaryAction: () => navigate('/admin/contact-requests')
        }}
      >
        <div className="text-center py-20">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Demande non trouvée</h2>
          <Button onClick={() => navigate('/admin/contact-requests')}>
            Retour aux demandes
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      headerConfig={{
        icon: fileTextIcon,
        iconColor: 'bg-white',
        title: `Demande de ${request.seeker_name}`,
        // subtitle: `ID: ${request.id}`,
        primaryAction: 'Tableau de Bord',
        secondaryAction: undefined,
        tertiaryAction: undefined,
        onPrimaryAction: () => navigate('/admin'),
        onSecondaryAction: undefined,
        onTertiaryAction: undefined
      }}
    >
      {/* <div className="mb-6 flex items-start justify-end">
        <div>
          {getScoreBadge(request.score)}
        </div>
      </div> */}

        <div className="space-y-6">
          {/* First Row: Identité and Bien Concerné - 50% each */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Identity Card */}
            <Card className="h-full">
              <CardContent className="p-6 h-full flex flex-col">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Contacts</h2>
                <div className="space-y-3 flex-1">
                
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm text-gray-600">Email</div>
                        <a href={`mailto:${request.seeker_email}`} className="font-semibold text-[#FF9F68] hover:underline">
                          {request.seeker_email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm text-gray-600">Téléphone</div>
                        <a href={`tel:${request.seeker_phone}`} className="font-semibold text-[#FF9F68] hover:underline">
                          {request.seeker_phone}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Message Client Button */}
                <div className="mt-4 pt-4 border-t">
                  <StartConversationButton
                    participantId={request.seekerId || request.seeker_id}
                    participantEmail={request.seeker_email}
                    participantName={request.seeker_name}
                    participantRole="CLIENT"
                    contactRequestId={id}
                    className="w-full"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Property Info Card */}
            {property && (
              <Card className="h-full">
                <CardContent className="p-6 h-full flex flex-col">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Bien Concerné</h2>
                  <div className="flex gap-4 flex-1">
                    {property.images && property.images[0] && (
                      <img
                        src={property.images[0]}
                        alt={property.title}
                        className="w-32 h-32 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="font-bold text-lg mb-2">{property.title}</h3>
                      <div className="text-2xl font-bold text-[#FF9F68] mb-2">
                        {formatPrice(property.price)}
                      </div>
                      <div className="text-sm text-gray-600">
                        <MapPin className="inline h-4 w-4 mr-1" />
                        {property.city}, {property.postal_code}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => window.open(`/properties/${property.id}`, '_blank')}
                      >
                        Voir l'annonce
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Second Row: Tabs - Full width */}
          {/* Tabs */}
          <Card>
              <CardContent className="p-6">
                <Tabs defaultValue="workflow">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="workflow">Workflow</TabsTrigger>
                    <TabsTrigger value="communication">Communication</TabsTrigger>
                    <TabsTrigger value="timeline">Timeline</TabsTrigger>
                  </TabsList>

                  {/* Workflow Tab */}
                  <TabsContent value="workflow" className="mt-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Section principale à gauche (50%) */}
                      <div className="border border-gray-200 rounded-lg p-6 space-y-4 relative">
                        {/* Bouton Modifier en bas à droite */}
                        <div className="absolute bottom-4 right-4">
                          {editMode ? (
                            <div className="flex gap-2">
                              <Button onClick={handleUpdate} size="sm" className="bg-[#FF9F68] hover:bg-[#E67E4F]">
                                <Save className="h-4 w-4 mr-2" />
                                Enregistrer
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>
                                Annuler
                              </Button>
                            </div>
                          ) : (
                            <Button onClick={() => setEditMode(true)} size="sm" variant="outline">
                              <Edit className="h-4 w-4 mr-2" />
                              Modifier
                            </Button>
                          )}
                        </div>

                        {/* Statut et Date d'emménagement sur la même ligne */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div>
                            <Label>Statut</Label>
                            {editMode ? (
                              <Select
                                value={formData.status}
                                onValueChange={(value) => setFormData({ ...formData, status: value })}
                                className="mt-2"
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="NEW">Nouveau</SelectItem>
                                  <SelectItem value="QUALIFIED">Qualifié</SelectItem>
                                  <SelectItem value="ASSIGNED">Assigné</SelectItem>
                                  <SelectItem value="VISIT_PLANNED">Visite Planifiée</SelectItem>
                                  <SelectItem value="COMPLETED">Converti</SelectItem>
                                  <SelectItem value="LOST">Perdu</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <div className="mt-2">
                                <Badge className={(() => {
                                  const statusColors = {
                                    'NEW': 'bg-green-100 text-green-800 border border-green-200',
                                    'QUALIFIED': 'bg-blue-100 text-blue-800 border border-blue-200',
                                    'ASSIGNED': 'bg-purple-100 text-purple-800 border border-purple-200',
                                    'VISIT_PLANNED': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
                                    'COMPLETED': 'bg-emerald-100 text-emerald-800 border border-emerald-200',
                                    'LOST': 'bg-red-100 text-red-800 border border-red-200'
                                  };
                                  return statusColors[request.status] || 'bg-gray-100 text-gray-800 border border-gray-200';
                                })()}>
                                  {request.status}
                                </Badge>
                              </div>
                            )}
                          </div>

                          {/* Date d'emménagement souhaitée */}
                          {request.move_in_date && (
                            <div>
                              <Label>Date d'emménagement souhaitée</Label>
                              <div className="mt-2 flex items-center">
                                <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                                <span className="font-semibold text-gray-900">
                                  {new Date(request.move_in_date).toLocaleDateString('fr-FR', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Notes Admin */}
                        <div>
                          <Label>Notes Admin</Label>
                          {editMode ? (
                            <Textarea
                              value={formData.admin_notes}
                              onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value })}
                              rows={4}
                              className="mt-2"
                            />
                          ) : (
                            <p className="mt-2 text-gray-700">
                              {request.admin_notes || (
                                <span className="text-gray-500 italic">
                                  Aucune note. Cliquer sur modifier pour en ajouter une
                                </span>
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Section droite (50%) - Assigner à un pro et Visite */}
                      <div className="space-y-6">
                        {/* Assignation Section */}
                        <div>
                          <h3 className="font-semibold mb-3">Assignation</h3>
                          {!request.assigned_to_admin && (
                            <Button onClick={handleAssignToMe} variant="outline" className="w-full mb-3">
                              <UserPlus className="h-4 w-4 mr-2" />
                              M'assigner cette demande
                            </Button>
                          )}

                          {/* If already assigned, show only message button */}
                          {request.assigned_to_professional_id ? (
                            <div>
                              {(() => {
                                // Use property.owner if available, otherwise fallback to assigned professional
                                const assignedPro = property?.owner || null;

                                const proName =
                                  assignedPro?.professionalInfo?.companyName ||
                                  assignedPro?.professional_info?.company_name ||
                                  `${assignedPro?.firstName || assignedPro?.first_name || ''} ${assignedPro?.lastName || assignedPro?.last_name || ''}`.trim() ||
                                  'Professionnel';

                                return (
                                  <StartConversationButton
                                    participantId={request.assigned_to_professional_id}
                                    participantEmail={assignedPro?.email}
                                    participantName={proName}
                                    participantRole="PROFESSIONAL"
                                    contactRequestId={id}
                                    className="w-full"
                                  />
                                );
                              })()}
                            </div>
                          ) : (
                            /* If not assigned, show assignment options */
                            <>
                              {/* Display professional creator info */}
                              {property?.owner && (
                                <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                                    Professionnel créateur du bien
                                  </Label>
                                  <div className="space-y-1">
                                    <div className="text-sm text-gray-900">
                                      <strong>
                                        {property.owner.professionalInfo?.companyName || 
                                         property.owner.professional_info?.company_name ||
                                         `${property.owner.firstName || property.owner.first_name || ''} ${property.owner.lastName || property.owner.last_name || ''}`.trim() ||
                                         'Professionnel'}
                                      </strong>
                                    </div>
                                    {property.owner.email && (
                                      <div className="text-xs text-gray-600">
                                        {property.owner.email}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Assign to Professional Button */}
                              <div className="space-y-2 mb-4">
                                <Button
                                  onClick={handleAssignToProfessional}
                                  disabled={!property?.ownerId && !property?.owner?.id}
                                  className="w-full bg-[#FF9F68] hover:bg-[#E67E4F]"
                                >
                                  <Building2 className="h-4 w-4 mr-2" />
                                  Assigner au Professionnel
                                </Button>
                              </div>
                            </>
                          )}
                        </div>

                        {/* Visit Section */}
                        <div>
                          <h3 className="font-semibold mb-3">Visite</h3>
                          {request.visit_scheduled ? (
                            <div className="space-y-3">
                              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-green-900">Visite planifiée</span>
                                  {request.visit_completed && (
                                    <Badge className="bg-green-600 text-white">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Effectuée
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-sm space-y-1">
                                  <div className="flex items-center text-gray-700">
                                    <Calendar className="h-4 w-4 mr-2" />
                                    {new Date(request.visit_date).toLocaleDateString('fr-FR', {
                                      weekday: 'long',
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}
                                  </div>
                                  <div className="flex items-center text-gray-700">
                                    <Clock className="h-4 w-4 mr-2" />
                                    {new Date(request.visit_date).toLocaleTimeString('fr-FR', {
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })} ({request.visit_duration} min)
                                  </div>
                                </div>
                                {request.visit_notes && (
                                  <div className="mt-2 text-sm text-gray-600">
                                    <strong>Notes:</strong> {request.visit_notes}
                                  </div>
                                )}
                              </div>
                              {!request.visit_completed && (
                                <Button
                                  variant="outline"
                                  className="w-full text-red-600 border-red-300 hover:bg-red-50"
                                  onClick={async () => {
                                    try {
                                      await axios.delete(`${API}/admin/contact-requests/${id}/cancel-visit`);
                                      toast({
                                        title: 'Visite annulée',
                                        description: 'La visite a été annulée'
                                      });
                                      fetchRequest();
                                    } catch (error) {
                                      console.error('Error cancelling visit:', error);
                                    }
                                  }}
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Annuler la visite
                                </Button>
                              )}
                              {request.visit_completed && (
                                <div className="space-y-2">
                                  <Button
                                    className="w-full bg-[#FF9F68] hover:bg-[#E67E4F] text-white"
                                    onClick={async () => {
                                      try {
                                        await axios.post(`${API}/admin/contact-requests/${id}/send-visit-followup`);
                                        toast({
                                          title: 'Relance envoyée',
                                          description: 'Un message de relance a été envoyé au client avec des boutons d\'action'
                                        });
                                        fetchRequest();
                                      } catch (error) {
                                        console.error('Error sending follow-up:', error);
                                        toast({
                                          title: 'Erreur',
                                          description: error.response?.data?.message || 'Impossible d\'envoyer la relance',
                                          variant: 'destructive'
                                        });
                                      }
                                    }}
                                  >
                                    <Send className="h-4 w-4 mr-2" />
                                    Relancer le client
                                  </Button>
                                  {(!request.visit_outcome || request.visit_outcome === 'INTERESTED') && (
                                    <div className="grid grid-cols-2 gap-2">
                                      <Button
                                        variant="outline"
                                        className="text-green-600 border-green-300 hover:bg-green-50"
                                        onClick={async () => {
                                          try {
                                            await axios.put(`${API}/admin/contact-requests/${id}/visit-complete`, null, {
                                              params: {
                                                visitFeedback: 'Client veut le bien (validation manuelle admin)',
                                                visitOutcome: 'OFFER_MADE',
                                                nextSteps: ''
                                              }
                                            });
                                            toast({
                                              title: 'Décision enregistrée',
                                              description: 'Le client veut le bien'
                                            });
                                            fetchRequest();
                                          } catch (error) {
                                            console.error('Error updating visit outcome:', error);
                                            toast({
                                              title: 'Erreur',
                                              description: error.response?.data?.message || 'Impossible de mettre à jour',
                                              variant: 'destructive'
                                            });
                                          }
                                        }}
                                      >
                                        Client veut le bien
                                      </Button>
                                      <Button
                                        variant="outline"
                                        className="text-red-600 border-red-300 hover:bg-red-50"
                                        onClick={async () => {
                                          try {
                                            await axios.put(`${API}/admin/contact-requests/${id}/visit-complete`, null, {
                                              params: {
                                                visitFeedback: 'Client ne veut pas le bien (validation manuelle admin)',
                                                visitOutcome: 'NOT_INTERESTED',
                                                nextSteps: ''
                                              }
                                            });
                                            toast({
                                              title: 'Décision enregistrée',
                                              description: 'Le client ne veut pas le bien'
                                            });
                                            fetchRequest();
                                          } catch (error) {
                                            console.error('Error updating visit outcome:', error);
                                            toast({
                                              title: 'Erreur',
                                              description: error.response?.data?.message || 'Impossible de mettre à jour',
                                              variant: 'destructive'
                                            });
                                          }
                                        }}
                                      >
                                        Client ne veut pas
                                      </Button>
                                    </div>
                                  )}
                                  {(request.visit_outcome === 'OFFER_MADE' || request.visit_outcome === 'INTERESTED') && (
                                    <Button
                                      className="w-full bg-green-600 hover:bg-green-700 text-white"
                                      onClick={async () => {
                                        if (!confirm('Êtes-vous sûr de vouloir finaliser cette transaction ? Le bien sera marqué comme vendu/loué et retiré des recherches.')) {
                                          return;
                                        }
                                        try {
                                          await axios.post(`${API}/admin/contact-requests/${id}/finalize-transaction`);
                                          toast({
                                            title: 'Transaction finalisée',
                                            description: 'Le bien a été marqué comme vendu/loué'
                                          });
                                          fetchRequest();
                                        } catch (error) {
                                          console.error('Error finalizing transaction:', error);
                                          toast({
                                            title: 'Erreur',
                                            description: error.response?.data?.message || 'Impossible de finaliser la transaction',
                                            variant: 'destructive'
                                          });
                                        }
                                      }}
                                    >
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Finaliser la transaction
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="font-semibold text-amber-900">
                                    {request.status === 'IN_PROCESS' || request.visit_status === 'PENDING_CLIENT'
                                      ? 'En traitement (en attente client)'
                                      : 'Aucune visite planifiée'}
                                  </span>
                                </div>
                                <p className="text-sm text-amber-800">
                                  Proposez un créneau de visite (slot du bien ou date libre). Le client devra confirmer.
                                </p>
                              </div>

                              <div className="space-y-2">
                                <Label>Choisir un créneau pré-défini (slots du bien)</Label>
                                <select
                                  className="w-full border rounded-lg px-3 py-2"
                                  value={selectedSlotId}
                                  onChange={(e) => setSelectedSlotId(e.target.value)}
                                >
                                  <option value="">Sélectionner un slot</option>
                                  {visitSlots.map((slot) => (
                                    <option key={slot.id} value={slot.id}>
                                      {['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][slot.dayOfWeek]} ·{' '}
                                      {minutesToTime(slot.startMinutes)} - {minutesToTime(slot.endMinutes)} ({slot.duration}m)
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <div>
                                  <Label>Ou bien fixer une date/heure</Label>
                                  <input
                                    type="datetime-local"
                                    className="w-full border rounded-lg px-3 py-2"
                                    value={manualVisitDate}
                                    onChange={(e) => setManualVisitDate(e.target.value)}
                                  />
                                </div>
                                <div>
                                  <Label>Durée (minutes)</Label>
                                  <Input
                                    type="number"
                                    min={15}
                                    max={240}
                                    value={manualVisitDuration}
                                    onChange={(e) => setManualVisitDuration(parseInt(e.target.value))}
                                  />
                                </div>
                                <div>
                                  <Label>Notes</Label>
                                  <Input
                                    placeholder="Infos complémentaires"
                                    value={manualVisitNotes}
                                    onChange={(e) => setManualVisitNotes(e.target.value)}
                                  />
                                </div>
                              </div>

                              <div className="flex gap-3">
                                <Button onClick={handleProposeVisit} className="bg-[#FF9F68] hover:bg-[#E67E4F]">
                                  <Send className="h-4 w-4 mr-2" />
                                  Proposer au client
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Communication Tab */}
                  <TabsContent value="communication" className="mt-6">
                    <div className="space-y-6">
                      <div>
                        <h3 className="font-semibold mb-2">Conversation initiale lors de la prise de contact.</h3>
                        {request.messages && request.messages.length > 0 ? (
                          <div className="space-y-3 max-h-80 overflow-y-auto">
                            {request.messages.map((msg, index) => {
                              const isAssistant = msg.author === 'ASSISTANT' || msg.author === 'assistant';
                              const createdAt = msg.createdAt || msg.created_at;
                              return (
                                <div
                                  key={index}
                                  className={`flex ${isAssistant ? 'justify-start' : 'justify-end'}`}
                                >
                                  <div
                                    className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                                      isAssistant
                                        ? 'bg-gray-50 border border-gray-200 text-gray-800'
                                        : 'bg-[#FF9F68] text-white'
                                    }`}
                                  >
                                    <p className="whitespace-pre-wrap leading-relaxed">
                                      {msg.content}
                                    </p>
                                    {createdAt && (
                                      <div className={`mt-1 text-[11px] ${isAssistant ? 'text-gray-500' : 'text-blue-100'}`}>
                                        {new Date(createdAt).toLocaleString('fr-FR')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">
                            Aucun historique de conversation n&apos;est disponible pour cette demande.
                          </p>
                        )}
                      </div>

                      {/* <div className="pt-4 border-t">
                        <h3 className="font-semibold mb-3">Communications Admin / Pro</h3>
                        <CommunicationPanel requestId={id} requestData={request} />
                      </div> */}
                    </div>
                  </TabsContent>

                  {/* Timeline Tab */}
                  <TabsContent value="timeline" className="mt-6">
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full mr-3"></div>
                        <div>
                          <div className="font-semibold">Demande créée</div>
                          <div className="text-sm text-gray-600">{formatDate(request.created_at)}</div>
                          <div className="text-xs text-gray-500">Via {request.source}</div>
                        </div>
                      </div>

                      {request.qualification_date && (
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-2 h-2 mt-2 bg-green-600 rounded-full mr-3"></div>
                          <div>
                            <div className="font-semibold">Demande qualifiée</div>
                            <div className="text-sm text-gray-600">{formatDate(request.qualification_date)}</div>
                          </div>
                        </div>
                      )}

                      {request.assignment_date && (
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-2 h-2 mt-2 bg-purple-600 rounded-full mr-3"></div>
                          <div>
                            <div className="font-semibold">Assignée à un professionnel</div>
                            <div className="text-sm text-gray-600">{formatDate(request.assignment_date)}</div>
                          </div>
                        </div>
                      )}

                      {request.visit_date && (
                        <div className="flex items-start">
                          <div className="flex-shrink-0 w-2 h-2 mt-2 bg-yellow-600 rounded-full mr-3"></div>
                          <div>
                            <div className="font-semibold">Visite planifiée</div>
                            <div className="text-sm text-gray-600">{formatDate(request.visit_date)}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
        </div>
    </Layout>
  );
};

export default ContactRequestDetailPage;
